/* eslint-disable @typescript-eslint/no-use-before-define */
import { ListMask } from '@euler/app/components/list/ListMask';
import { Typography } from '@euler/app/components/typography';
import { useTaskHeaderMetrics } from '@euler/app/flows/task/functions/useTaskHeaderMetrics';
import { useIsAsideLayout } from '@euler/components/Aside';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { FC, memo, ReactNode, useCallback, useRef } from 'react';
import {
  LayoutChangeEvent,
  LayoutRectangle,
  StyleProp,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Animated, {
  interpolateColor,
  scrollTo,
  SharedValue,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { css, useTheme } from 'styled-components';

type SearchInputProps = {
  searchInputEditable?: boolean;
  searchText?: string;
  onSearchInputPress?: () => void;
  onSearchInputChange?: (text: string) => void;
  onMorePress?: () => void;
};

type ScrollerProps = {
  activeIndex?: SharedValue<number>;
  scrollerItems?: string[];
  onScrollerItemPress?: (index: number) => void;
};

type Props = {
  showBluetoothIcon?: boolean;
  hideRightButtons?: boolean;
  technicianName?: string;
  onLayout?: (e: LayoutChangeEvent) => void;
  onTechnicianPress?: () => void;
} & SearchInputProps &
  ScrollerProps;

const kSearchInputHeight = 36;
const kNavButtonSize = 36;

export const InspectionNavHeader = memo(
  ({
    activeIndex,
    scrollerItems,
    technicianName,
    onTechnicianPress,
    onLayout,
    onScrollerItemPress,
    ...searchInputProps
  }: Props) => {
    const { rpx } = useResponsiveContext();
    const isAside = useIsAsideLayout();
    const defaultActiveIndex = useSharedValue(0);
    const { navBarHeight, safeAreaInsets, statusBarHeight } =
      useTaskHeaderMetrics();
    const theme = useTheme();
    return (
      <View
        css={`
          background-color: ${theme.inspection.siteGrid.headerBgColor};
        `}
        onLayout={onLayout}
      >
        {/** shade */}
        <LinearGradient
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 1 }}
          colors={['#006be4', '#5ba0e0']}
          locations={[0.335, 1]}
          style={StyleSheet.absoluteFill}
          css={`
            flex: 1;
          `}
        />

        {isAside ? (
          <View
            css={`
              height: ${statusBarHeight}px;
            `}
          />
        ) : null}

        {/** search bar */}
        <SearchNavBar
          css={`
            height: ${navBarHeight - (isAside ? statusBarHeight : 0)}px;
            padding-top: ${safeAreaInsets.top}px;
            margin-bottom: ${rpx(12)}px;
          `}
          {...searchInputProps}
        />

        {/** scroller */}
        {(scrollerItems != null && scrollerItems.length > 0) ||
        technicianName != null ? (
          <HStack
            css={`
              margin-bottom: ${rpx(10)}px;
            `}
          >
            <ListMask
              leadingMask={false}
              trailingMask
              css={`
                flex: 1;
              `}
            >
              <Scroller
                activeIndex={activeIndex ?? defaultActiveIndex}
                items={scrollerItems ?? []}
                onItemPress={onScrollerItemPress}
              />
            </ListMask>
            {technicianName != null && (
              <TouchableOpacity
                onPress={onTechnicianPress}
                css={`
                  padding: ${rpx(4)}px ${rpx(16)}px;
                `}
              >
                <Typography.Label size={rpx(14)} color={Colors.White5}>
                  {`技师: ${technicianName}`}
                </Typography.Label>
              </TouchableOpacity>
            )}
          </HStack>
        ) : null}
      </View>
    );
  },
);

const ScrollerItem = memo(
  ({
    text,
    index,
    activeIndex,
    onLayout,
    onPress,
  }: {
    text: string;
    index: number;
    activeIndex: SharedValue<number>;
    onLayout: (index: number, layout: LayoutRectangle) => void;
    onPress?: (index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const isActive = useDerivedValue(() =>
      index === activeIndex.value ? 1 : 0,
    );
    const progress = useDerivedValue(() =>
      withTiming(isActive.value, { duration: 200 }),
    );
    const style = useAnimatedStyle(() => {
      return {
        backgroundColor: interpolateColor(
          progress.value,
          [0, 1],
          ['transparent', '#046ad6'],
        ),
        borderColor: interpolateColor(
          progress.value,
          [0, 1],
          ['rgba(255, 255, 255, 0.15)', '#fff'],
        ),
      };
    });

    const onInternalPress = useCallback(() => {
      onPress?.(index);
    }, [index, onPress]);

    const onInternalLayout = useCallback(
      (e: LayoutChangeEvent) => {
        onLayout(index, e.nativeEvent.layout);
      },
      [index, onLayout],
    );

    return (
      <Animated.View
        style={style}
        css={`
          margin-right: ${rpx(10)}px;
          height: ${rpx(26)}px;
          border-radius: ${rpx(13)}px;
          border-width: 1px;
        `}
        onLayout={onInternalLayout}
      >
        <TouchableOpacity
          css={`
            flex: 1;
            padding: 0 ${rpx(12)}px;
            align-items: center;
            justify-content: center;
          `}
          onPress={onInternalPress}
        >
          <Typography.Label light size={rpx(14)} color="#fff">
            {text}
          </Typography.Label>
        </TouchableOpacity>
      </Animated.View>
    );
  },
);

const Scroller = memo(
  ({
    activeIndex,
    items,
    style,
    onItemPress,
  }: {
    activeIndex: SharedValue<number>;
    items: string[];
    style?: StyleProp<ViewStyle>;
    onItemPress?: (index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const layouts = useSharedValue<LayoutRectangle[]>([]);
    const calculatedLayouts = useRef<LayoutRectangle[]>([]).current;
    const onItemLayout = useCallback(
      (index: number, layout: LayoutRectangle) => {
        if (calculatedLayouts.length !== items.length) {
          calculatedLayouts.length = items.length;
        }
        calculatedLayouts[index] = layout;
        for (let i = 0; i < items.length; i++) {
          if (!calculatedLayouts[i]) return;
        }
        layouts.value = calculatedLayouts.map(x => x);
      },
      [calculatedLayouts, items.length, layouts],
    );

    const scrollViewRef = useAnimatedRef<ScrollView>();
    const scrollOffset = useDerivedValue(
      () => layouts.value[activeIndex.value],
    );
    const paddingRight = rpx(10);
    useDerivedValue(() => {
      if (!scrollOffset.value) return;
      scrollTo(
        scrollViewRef,
        scrollOffset.value.x - paddingRight,
        scrollOffset.value.y,
        true,
      );
    });

    return (
      <ScrollView
        horizontal
        ref={scrollViewRef}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingTop: rpx(8),
          paddingBottom: rpx(8),
          paddingLeft: rpx(10),
          paddingRight: rpx(10),
        }}
        style={style}
      >
        {items.map((text, index) => (
          <ScrollerItem
            key={index}
            index={index}
            text={text}
            activeIndex={activeIndex}
            onLayout={onItemLayout}
            onPress={onItemPress}
          />
        ))}
      </ScrollView>
    );
  },
);

const NavButton: FC<{
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  children?: ReactNode;
}> = memo(({ style, onPress, children }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      onPress={onPress}
      css={`
        padding-left: ${rpx(8)}px;
        padding-right: ${rpx(8)}px;
        height: ${rpx(kNavButtonSize)}px;
        align-items: center;
        justify-content: center;
      `}
      style={style}
    >
      {children}
    </TouchableOpacity>
  );
});

const SearchBox = memo(
  ({
    onSearchInputPress,
    searchText,
    searchInputEditable,
    onSearchInputChange,
  }: SearchInputProps) => {
    const { rpx } = useResponsiveContext();
    const wrapperCss = css`
      background-color: #fff;
      height: ${rpx(kSearchInputHeight)}px;
      border-radius: ${rpx(kSearchInputHeight) / 2}px;
      padding: 0 ${rpx(20)}px;
      flex: 1;
    `;

    if (searchInputEditable) {
      return (
        <View
          css={`
            ${wrapperCss}
          `}
        >
          <TextInput
            placeholder="搜索检测项目或指标"
            value={searchText ?? ''}
            onChangeText={onSearchInputChange}
            autoFocus
            css={`
              flex: 1;
            `}
          />
        </View>
      );
    }
    return (
      <TouchableOpacity
        onPress={onSearchInputPress}
        activeOpacity={0.9}
        css={`
          ${wrapperCss};
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
        `}
      >
        <Icon name="search" size={rpx(16)} color="#999" />
        <Typography.Label
          size={rpx(14)}
          light
          color="#999"
          css={`
            margin-left: ${rpx(10)}px;
          `}
        >
          搜索检测项目或指标
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);

const SearchNavBar = memo(
  ({
    showBluetoothIcon,
    hideRightButtons,
    style,
    onMorePress,
    ...searchInputProps
  }: {
    showBluetoothIcon?: boolean;
    hideRightButtons?: boolean;
    style?: StyleProp<ViewStyle>;
  } & SearchInputProps) => {
    const navigation = useNavigation();
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={style}
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          flex-wrap: nowrap;
        `}
      >
        <NavButton onPress={() => navigation.goBack()}>
          <Icon name="chevron-left" size={rpx(34)} color="#fff" />
        </NavButton>
        <SearchBox {...searchInputProps} />
        {hideRightButtons ? (
          <View
            css={`
              width: ${rpx(kNavButtonSize) + rpx(10)}px;
            `}
          />
        ) : (
          <>
            {showBluetoothIcon && (
              <NavButton>
                <Icon name="bluetooth-o" size={rpx(20)} color="#fff" />
              </NavButton>
            )}
            <NavButton
              onPress={onMorePress}
              css={`
                margin-right: ${rpx(8)}px;
              `}
            >
              <Icon name="more-vertical" size={rpx(24)} color="#fff" />
            </NavButton>
          </>
        )}
      </View>
    );
  },
);
